<script lang="ts">
  /**
   * Panel aditivo de IA en consulta.
   * - Graba/transcribe con SpeechRecognition del navegador (no sube audio).
   * - Estructura con backend → borrador editable.
   * - "Aplicar a ficha" solo rellena el formulario; el médico guarda con el botón normal.
   */
  import { ConsultaIa, type BorradorConsultaIa } from '../lib/api';

  type Props = {
    onAplicar: (b: BorradorConsultaIa) => void;
    /** Texto del botón de aplicar (ficha vs nota de evolución). */
    applyLabel?: string;
    /** Hint de persistencia (no se guarda hasta la acción del médico). */
    persistHint?: string;
  };
  let {
    onAplicar,
    applyLabel = 'Aplicar a ficha',
    persistHint = 'Nada se guarda hasta que usted pulse «Guardar ficha clínica».',
  }: Props = $props();

  let iaEnabled = $state<boolean | null>(null);
  let grabando = $state(false);
  let estructurando = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let transcripcion = $state('');
  let borrador = $state<BorradorConsultaIa | null>(null);
  let rec: any = null;

  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : null;

  $effect(() => {
    void ConsultaIa.estado()
      .then(s => { iaEnabled = !!s.enabled; })
      .catch(() => { iaEnabled = false; });
  });

  function stopRec() {
    try { rec?.stop?.(); } catch { /* ignore */ }
    rec = null;
    grabando = false;
  }

  function startRec() {
    error = null;
    info = null;
    if (!SpeechRecognition) {
      error = 'Este navegador no soporta reconocimiento de voz. Puede pegar la transcripción a mano.';
      return;
    }
    stopRec();
    const r = new SpeechRecognition();
    r.lang = 'es-GT';
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (ev: any) => {
      let chunk = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) chunk += ev.results[i][0].transcript + ' ';
      }
      if (chunk.trim()) {
        transcripcion = (transcripcion ? transcripcion + ' ' : '') + chunk.trim();
      }
    };
    r.onerror = () => { grabando = false; };
    r.onend = () => {
      // Si el usuario sigue en modo grabación, reinicia (Chrome corta a menudo).
      if (grabando) {
        try { r.start(); } catch { grabando = false; }
      }
    };
    rec = r;
    grabando = true;
    try { r.start(); } catch (e: any) {
      grabando = false;
      error = e?.message ?? 'No se pudo iniciar el micrófono.';
    }
  }

  function toggleGrabar() {
    if (grabando) stopRec();
    else startRec();
  }

  async function estructurar() {
    error = null;
    info = null;
    borrador = null;
    const t = transcripcion.trim();
    if (t.length < 20) {
      error = 'Necesita al menos unas frases en la transcripción.';
      return;
    }
    if (grabando) stopRec();
    estructurando = true;
    try {
      const r = await ConsultaIa.estructurar(t);
      borrador = r;
      info = r.fromAI
        ? `Borrador listo. Revise y pulse «${applyLabel}» (aún no se guarda).`
        : (r.mensaje ?? 'Borrador básico listo. Revise antes de aplicar.');
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      estructurando = false;
    }
  }

  function aplicar() {
    if (!borrador) return;
    onAplicar(borrador);
    info = `Campos aplicados. ${persistHint}`;
  }

  function limpiar() {
    stopRec();
    transcripcion = '';
    borrador = null;
    error = null;
    info = null;
  }
</script>

<div class="ia-panel" role="region" aria-label="IA de consulta">
  <div class="ia-head">
    <strong>IA de consulta</strong>
    <span class="badge" class:on={iaEnabled === true} class:off={iaEnabled === false}>
      {#if iaEnabled === null}…{:else if iaEnabled}Gemini activo{:else}Solo transcripción{/if}
    </span>
  </div>
  <p class="hint">
    Grabe la conversación (el audio no se sube: solo texto). Luego estructure un borrador.
    {persistHint}
  </p>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <label class="field">
    <span>Transcripción</span>
    <textarea rows="4" bind:value={transcripcion} placeholder="Hable o pegue aquí el texto de la consulta…"></textarea>
  </label>

  <div class="actions">
    <button type="button" class="mic" class:on={grabando} onclick={toggleGrabar} disabled={!SpeechRecognition && !grabando}>
      {grabando ? '⏹ Detener' : '🎤 Grabar consulta'}
    </button>
    <button type="button" class="primary" onclick={estructurar} disabled={estructurando || transcripcion.trim().length < 20}>
      {estructurando ? 'Estructurando…' : 'Estructurar borrador'}
    </button>
    <button type="button" class="ghost" onclick={limpiar}>Limpiar</button>
  </div>

  {#if borrador}
    <div class="borrador">
      <h4>Borrador (editable antes de aplicar)</h4>
      <label class="field"><span>Motivo</span><textarea rows="2" bind:value={borrador.motivoConsulta}></textarea></label>
      <label class="field"><span>HEA</span><textarea rows="3" bind:value={borrador.historiaEnfermedadActual}></textarea></label>
      <label class="field"><span>Diagnóstico (borrador)</span><textarea rows="2" bind:value={borrador.diagnostico}></textarea></label>
      <label class="field"><span>Alergias</span><textarea rows="1" bind:value={borrador.alergias}></textarea></label>
      <label class="field"><span>Antecedentes personales</span><textarea rows="2" bind:value={borrador.antecedentesPersonales}></textarea></label>
      {#if borrador.dxDiferenciales?.length}
        <p class="sug"><strong>Dx diferenciales (sugerencia):</strong> {borrador.dxDiferenciales.join(' · ')}</p>
      {/if}
      {#if borrador.labsSugeridos?.length}
        <p class="sug"><strong>Labs sugeridos (no se ordenan solos):</strong> {borrador.labsSugeridos.join(' · ')}</p>
      {/if}
      <button type="button" class="primary" onclick={aplicar}>{applyLabel}</button>
    </div>
  {/if}
</div>

<style>
  .ia-panel {
    border: 1px solid #bbf7d0;
    background: #f0fdf4;
    border-radius: 12px;
    padding: .85rem 1rem;
    margin: 0 0 1rem;
  }
  .ia-head { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; }
  .ia-head strong { color: #166534; font-size: .95rem; }
  .badge {
    font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
    padding: .15rem .45rem; border-radius: 999px; border: 1px solid #86efac; color: #166534; background: #fff;
  }
  .badge.off { border-color: #fcd34d; color: #92400e; background: #fffbeb; }
  .hint { margin: .35rem 0 .6rem; font-size: .82rem; color: #4b5563; }
  .field { display: flex; flex-direction: column; gap: .25rem; margin-bottom: .5rem; }
  .field span { font-size: .7rem; font-weight: 600; color: #6b7280; text-transform: uppercase; }
  textarea {
    width: 100%; box-sizing: border-box; resize: vertical;
    padding: .45rem .6rem; border: 1px solid #d1d5db; border-radius: 8px; font: inherit;
  }
  .actions { display: flex; flex-wrap: wrap; gap: .45rem; margin: .4rem 0 .6rem; }
  button {
    padding: .4rem .75rem; border-radius: 8px; border: 0; font: inherit; font-weight: 600; cursor: pointer;
  }
  button:disabled { opacity: .55; cursor: not-allowed; }
  .primary { background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; }
  .ghost { background: #fff; border: 1px solid #d1d5db; color: #374151; }
  .mic { background: #fff; border: 1px solid #86efac; color: #166534; }
  .mic.on { background: #dc2626; border-color: #dc2626; color: #fff; }
  .err { color: #dc2626; font-weight: 600; font-size: .85rem; }
  .ok { color: #166534; font-weight: 600; font-size: .85rem; }
  .borrador {
    margin-top: .5rem; padding-top: .65rem; border-top: 1px dashed #86efac;
  }
  .borrador h4 { margin: 0 0 .5rem; font-size: .9rem; color: #166534; }
  .sug { font-size: .82rem; color: #374151; margin: .25rem 0; }
</style>
